import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';
import { type ModuleTier } from '@/lib/featureFlags';
import { useTenantTier } from '@/hooks/useTenantTier';
import {
  LayoutDashboard,
  Building2,
  HardHat,
  Receipt,
  BarChart3,
  AlertTriangle,
  FileText,
  Shield,
  Users,
  Settings,
  LogOut,
  X,
  ChevronLeft,
  ChevronRight,
  ShieldAlert,
  Map,
  Network,
  Plug,
  Activity,
  FlaskConical,
  ClipboardList,
  Archive,
  Lock,
  Box,
  Upload,
  Brain,
  Gauge,
  Briefcase,
  Link2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import logoNetuno from '@/assets/logo_netuno.png';

interface EmpNavItem {
  label: string;
  href: string;
  icon: React.ElementType;
  badge?: number;
  roles?: string[];
  /** Module tier for gating (tier-based modules) */
  tier?: ModuleTier;
  /** Addon ID for gating (addon-based modules) */
  addonId?: string;
}

interface EmpNavSection {
  label: string;
  items: EmpNavItem[];
}

// IMPORTANTE: roles abaixo devem espelhar EXATAMENTE MODULE_VIEW_PERMISSIONS
// (src/contexts/AuthContext.tsx) e os requiredRoles em ProtectedRoute (src/App.tsx).
// Qualquer divergência expõe item de menu que leva a /acesso-negado.
const empNavSections: EmpNavSection[] = [
  {
    label: 'OPERAÇÃO DE CONTRATOS',
    items: [
      // { label: 'Monitoramento Operacional', href: '/empreendimentos/monitoramento', icon: LayoutDashboard, roles: ['admin','coadmin','admin_cluster','gestor_planejamento','gestor_qualidade','gestor_documentacao','apontador','observador','gestor','executor','gestor_execucao'], tier: 'basico' },
      // execucao: MODULE_VIEW_PERMISSIONS['execucao']
      { label: 'Portal da Execução', href: '/empreendimentos/execucao', icon: HardHat, roles: ['admin','coadmin','admin_cluster','gestor_planejamento','gestor_qualidade','gestor_documentacao','apontador','gestor','executor','gestor_execucao'], tier: 'enterprise' },
      // quality-gate: SoD — apontador NÃO vê (não pode acessar); inclui gestor_execucao read-only
      { label: 'Portal da Qualidade', href: '/empreendimentos/quality-gate', icon: ShieldAlert, roles: ['admin','coadmin','admin_cluster','gestor_planejamento','gestor_qualidade','gestor_documentacao','gestor','gestor_execucao'], tier: 'enterprise' },
      // planning-gate: SoD — apontador e gestor_qualidade NÃO veem; inclui gestor_execucao read-only
      { label: 'Portal da Planejamento', href: '/empreendimentos/planning-gate', icon: ClipboardList, roles: ['admin','coadmin','admin_cluster','gestor_planejamento','gestor_documentacao','gestor','gestor_execucao'], tier: 'enterprise' },
      // medicoes: inclui gestor_execucao read-only
      { label: 'Portal de Medições', href: '/empreendimentos/medicoes', icon: Receipt, roles: ['admin','coadmin','admin_cluster','gestor_planejamento','gestor','gestor_execucao'], tier: 'enterprise' },
      // { label: 'Arquivo & Data Books', href: '/empreendimentos/arquivo', icon: Archive, roles: ['admin','coadmin','admin_cluster','gestor_planejamento','gestor_qualidade','gestor_documentacao','apontador','gestor','executor'], tier: 'profissional' },
      // { label: 'Visualização BIM', href: '/empreendimentos/bim', icon: Box, roles: ['admin', 'coadmin', 'admin_cluster'], addonId: 'addon_bim' },
    ],
  },
  {
    label: 'GESTÃO DE CARTEIRA',
    items: [
      // contratos: MODULE_VIEW_PERMISSIONS['contratos'] — inclui gestor_execucao
      { label: 'Contratos', href: '/empreendimentos/contratos', icon: Building2, roles: ['admin','coadmin','admin_cluster','gestor_planejamento','gestor_qualidade','gestor_documentacao','apontador','gestor','executor','gestor_execucao'], tier: 'profissional' },
      // mapa: MODULE_VIEW_PERMISSIONS['mapa'] — inclui gestor_execucao
      { label: 'Inteligência Geoespacial', href: '/empreendimentos/mapa', icon: Map, roles: ['admin','coadmin','admin_cluster','gestor_planejamento','gestor_qualidade','gestor_documentacao','apontador','gestor','executor','gestor_execucao'], tier: 'basico' },
      { label: 'Painel Executivo', href: '/empreendimentos/portfolio', icon: BarChart3, roles: ['admin','coadmin','admin_cluster','gestor_execucao','gestor_planejamento','gestor_qualidade','gestor','observador'], tier: 'enterprise' },
      // { label: 'Exploração Operacional', href: '/empreendimentos/exploracao', icon: Activity, roles: ['admin','coadmin','admin_cluster'], tier: 'enterprise' },
      { label: 'Inteligência de Risco', href: '/empreendimentos/inteligencia', icon: Brain, roles: ['admin','coadmin','admin_cluster','gestor_execucao','gestor_planejamento','gestor_qualidade','gestor'], tier: 'enterprise' },
      // [OCULTO] Analytics Operacional e Alertas — removidos do menu para ocultação segura (código preservado para reativação futura)
      // { label: 'Analytics Operacional', href: '/empreendimentos/risk-analytics', icon: Activity, roles: ['admin', 'coadmin', 'admin_cluster', 'gestor_execucao', 'gestor_planejamento', 'gestor_qualidade', 'gestor'], tier: 'enterprise' },
      // { label: 'Alertas', href: '/empreendimentos/alertas', icon: AlertTriangle, roles: ['admin','coadmin','admin_cluster','gestor_planejamento','gestor_qualidade','gestor'], tier: 'enterprise' },
      // { label: 'Relatórios', href: '/empreendimentos/relatorios', icon: FileText, roles: ['admin','coadmin','admin_cluster','gestor_planejamento','gestor'], tier: 'enterprise' },
      { label: 'Timeline de Decisões', href: '/empreendimentos/timeline-decisoes', icon: ClipboardList, roles: ['admin','coadmin','admin_cluster','gestor_planejamento','gestor_qualidade','gestor'], tier: 'enterprise' },
      { label: 'Convites para IFs', href: '/empreendimentos/convites-if', icon: Link2, roles: ['admin', 'coadmin', 'admin_cluster'], tier: 'enterprise' },
    ],
  },
  {
    label: 'GOVERNANÇA DO SISTEMA',
    items: [
      { label: 'Logs Sistêmicos', href: '/empreendimentos/auditoria', icon: Shield, roles: ['admin', 'coadmin', 'admin_cluster'], tier: 'enterprise' },
      { label: 'Observabilidade', href: '/empreendimentos/observabilidade', icon: Gauge, roles: ['admin', 'coadmin'], tier: 'enterprise' },
      { label: 'Clusters', href: '/empreendimentos/clusters', icon: Network, roles: ['admin', 'coadmin'], tier: 'profissional' },
      { label: 'Usuários', href: '/empreendimentos/usuarios', icon: Users, roles: ['admin', 'coadmin', 'admin_cluster'], tier: 'enterprise' },
      // { label: 'Configurações', href: '/empreendimentos/configuracoes', icon: Settings, roles: ['admin', 'coadmin'], tier: 'enterprise' },
      // { label: 'Ferramentas Dev', href: '/empreendimentos/ferramentas-dev', icon: FlaskConical, roles: ['admin', 'coadmin'], tier: 'enterprise' },
    ],
  },
];

// Financing section — imported from module config
import { financiamentoNavSection } from '@/modules/financiamento/navigation/financiamentoNavConfig';
const financiamentoSection: EmpNavSection = {
  label: financiamentoNavSection.label,
  items: financiamentoNavSection.items.map(item => ({
    label: item.label,
    href: item.href,
    icon: item.icon,
    roles: item.roles,
  })),
};

interface EmpSidebarProps {
  isMobile?: boolean;
  onCloseMobile?: () => void;
}

const EmpSidebar: React.FC<EmpSidebarProps> = ({ isMobile = false, onCloseMobile }) => {
  const [collapsed, setCollapsed] = React.useState(false);
  const location = useLocation();
  const { role, signOut } = useAuth();
  const { checkModuleAccess, hasAddon } = useTenantTier();
  const scrollAreaRef = React.useRef<HTMLDivElement | null>(null);
  const scrollViewportRef = React.useRef<HTMLElement | null>(null);
  const sidebarScrollStorageKey = 'netuno:emp-sidebar-scroll-top';

  const persistSidebarScroll = React.useCallback(() => {
    const scrollTop = scrollViewportRef.current?.scrollTop ?? 0;
    window.sessionStorage.setItem(sidebarScrollStorageKey, String(scrollTop));
  }, [sidebarScrollStorageKey]);

  React.useEffect(() => {
    const viewport = scrollAreaRef.current?.querySelector('[data-radix-scroll-area-viewport]') as HTMLElement | null;
    if (!viewport) return;

    scrollViewportRef.current = viewport;

    const persisted = Number(window.sessionStorage.getItem(sidebarScrollStorageKey) ?? '0');
    if (Number.isFinite(persisted) && persisted >= 0) {
      viewport.scrollTop = persisted;
    }

    const onScroll = () => {
      window.sessionStorage.setItem(sidebarScrollStorageKey, String(viewport.scrollTop));
    };

    viewport.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      viewport.removeEventListener('scroll', onScroll);
    };
  }, [sidebarScrollStorageKey]);

  const filterItems = (items: EmpNavItem[]) =>
    items.filter(item => {
      if (!item.roles) return true;
      if (!role) return false;
      return item.roles.includes(role);
    });

  const handleNavClick = () => {
    persistSidebarScroll();
    if (isMobile && onCloseMobile) onCloseMobile();
  };

  const isCollapsed = isMobile ? false : collapsed;

  const isActive = (href: string) => {
    if (href === '/empreendimentos') return location.pathname === '/empreendimentos';
    if (href.startsWith('/financiamento/')) return location.pathname === href;
    return location.pathname.startsWith(href);
  };

  // Determine if a section has any visible items
  const allSections = [empNavSections[0], empNavSections[1], financiamentoSection, empNavSections[2]];
  const visibleSections = allSections
    .map(section => ({ ...section, items: filterItems(section.items) }))
    .filter(section => section.items.length > 0);

  return (
    <aside
      className={cn(
        'emp-sidebar h-screen flex flex-col transition-[width] duration-300',
        isMobile ? 'w-[280px] max-w-[85vw]' : 'fixed left-0 top-0 z-50',
        !isMobile && (isCollapsed ? 'w-[72px]' : 'w-64')
      )}
    >
      {/* Header */}
      <div className="px-5 py-5 border-b border-white/10 flex items-center justify-between min-h-[68px]">
        <div className={cn('flex min-w-0 items-center gap-2', isCollapsed && 'justify-center')}>
          <img src={logoNetuno} alt="NetUno" className={cn('object-contain flex-shrink-0', isCollapsed ? 'w-16 h-16' : 'w-14 h-14')} />
          {!isCollapsed && (
            <div className="min-w-0 max-w-[132px]">
              <h1
                className="font-bold leading-tight tracking-[0.15em] text-lg"
                style={{ color: 'hsl(var(--brand))', fontFamily: 'Manrope, sans-serif' }}
              >
                NetUno
              </h1>
              <p className="mt-0.5 whitespace-nowrap text-[9px] font-medium leading-none tracking-[0.04em]" style={{ color: 'hsl(0, 0%, 60%)' }}>
                Previsibilidade com Lastro
              </p>
            </div>
          )}
        </div>
        {isMobile && (
          <Button
            variant="ghost"
            size="icon"
            onClick={onCloseMobile}
            className="text-white/70 hover:text-white hover:bg-white/10 min-h-[44px] min-w-[44px]"
          >
            <X className="w-5 h-5" strokeWidth={1.5} />
          </Button>
        )}
      </div>

      {/* Navigation */}
      <ScrollArea ref={scrollAreaRef} className="flex-1 px-3 py-4">
        <nav className="space-y-5">
          {visibleSections.map((section, sIdx) => (
            <div key={section.label}>
              {/* Section label — hidden when collapsed */}
              {!isCollapsed && (
                <p className="px-3 mb-1.5 text-xs font-semibold tracking-widest uppercase"
                  style={{ color: 'hsl(0, 0%, 45%)' }}>
                  {section.label}
                </p>
              )}
              {/* Divider for collapsed state */}
              {isCollapsed && sIdx > 0 && (
                <div className="border-t border-white/10 mb-2" />
              )}
              <div className="space-y-0.5">
                {section.items.map((item) => {
                  const active = isActive(item.href);
                  
                  // Determine accessibility based on tier or addon
                  let accessible: boolean;
                  if (item.addonId) {
                    // Addon-gated: check via hasAddon
                    accessible = hasAddon(item.addonId);
                  } else {
                    // Tier-gated: check via checkModuleAccess
                    const moduleId = item.href.replace('/empreendimentos/', '').split('/')[0] || 'dashboard';
                    accessible = !item.tier || checkModuleAccess(moduleId);
                  }

                  if (!accessible) {
                    // Render disabled/locked item
                    return (
                      <div
                        key={item.href}
                        title={isCollapsed ? `${item.label} (Indisponível)` : undefined}
                        className={cn(
                          'flex items-center gap-3 px-3 py-2.5 rounded-lg min-h-[40px]',
                          'text-white/25 cursor-not-allowed border-l-[3px] border-l-transparent'
                        )}
                      >
                        <item.icon className="w-[18px] h-[18px] flex-shrink-0 text-white/20" strokeWidth={1.5} />
                        {!isCollapsed && (
                          <>
                            <span className="flex-1 text-sm truncate font-normal">{item.label}</span>
                            <Lock className="w-3.5 h-3.5 text-white/20 flex-shrink-0" />
                          </>
                        )}
                      </div>
                    );
                  }

                    return (
                      <NavLink
                        key={item.href}
                        to={item.href}
                        end={item.href === '/empreendimentos' || item.href.startsWith('/financiamento/')}
                        preventScrollReset
                        onClick={handleNavClick}
                        title={isCollapsed ? item.label : undefined}
                        className={cn(
                          'flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-150 group min-h-[40px]',
                          'active:scale-[0.98]',
                          active
                            ? 'text-white border-l-[3px] border-l-[hsl(162,46%,33%)] bg-white/10'
                            : 'text-white/60 hover:text-white/90 hover:bg-white/8 border-l-[3px] border-l-transparent'
                        )}
                      >
                        <item.icon
                          className={cn(
                            'w-[18px] h-[18px] flex-shrink-0 transition-colors',
                            active ? 'text-emp-primary' : 'text-white/50 group-hover:text-white/80'
                          )}
                          strokeWidth={1.5}
                        />
                        {!isCollapsed && (
                          <span className={cn('flex-1 text-sm truncate', active ? 'font-semibold' : 'font-normal')}>
                            {item.label}
                          </span>
                        )}
                        {!isCollapsed && item.badge && (
                          <Badge className="text-xs bg-emp-primary/20 text-emp-primary border-0">
                            {item.badge}
                          </Badge>
                        )}
                      </NavLink>
                    );
                })}
              </div>
            </div>
          ))}
        </nav>
      </ScrollArea>

      {/* Bottom section */}
      <div className="px-3 py-4 border-t border-white/8">
        <button
          onClick={() => { handleNavClick(); signOut(); }}
          className={cn(
            'flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-150 w-full min-h-[40px]',
            'text-white/50 hover:text-white/80 hover:bg-white/6',
            'active:scale-[0.98]'
          )}
        >
          <LogOut className="w-[18px] h-[18px] flex-shrink-0" strokeWidth={1.5} />
          {!isCollapsed && <span className="text-sm">Sair</span>}
        </button>
      </div>

      {/* Collapse button — desktop only */}
      {!isMobile && (
        <Button
          variant="ghost"
          size="icon"
          className="absolute -right-3.5 top-20 bg-card border shadow-md hover:bg-muted rounded-full h-7 w-7"
          onClick={() => setCollapsed(!collapsed)}
        >
          {isCollapsed
            ? <ChevronRight className="w-3.5 h-3.5 text-foreground" />
            : <ChevronLeft className="w-3.5 h-3.5 text-foreground" />}
        </Button>
      )}
    </aside>
  );
};

export default EmpSidebar;
